import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { BbitString, IBbitI18n } from '@bbitgmbh/bbit.utils';
import { BbitWebShopAlgoliaService, BbitWebShopService } from '@bbitgmbh/webshop.sdk';
import { I18nPipe } from '@bbitgmbh/website.sdk';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

export interface IBreadcrumb {
  facet?: string;
  text: IBbitI18n;
  value?: string;
}

@Component({
  selector: 'app-item-list-header',
  templateUrl: './item-list-header.component.html',
  styleUrls: ['./item-list-header.component.scss'],
  imports: [AsyncPipe, I18nPipe],
})
export class ItemListHeaderComponent implements OnDestroy {
  algolia = inject(BbitWebShopAlgoliaService);
  private _webShop = inject(BbitWebShopService);

  private _ngUnsubscribe = new Subject<void>();
  public breadcrumbs = new BehaviorSubject<IBreadcrumb[]>([]);
  private _query = '';
  public title = new BehaviorSubject<IBbitI18n>({ key: 'allProducts' });
  public showResultCount = new BehaviorSubject(false);

  constructor() {
    const allCategories = this._webShop.getCategories();
    this.algolia
      .observeRawCategoryFilters()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((filters) => {
        const breadcrumbs: IBreadcrumb[] = [];

        for (const o of filters
          .filter((o) => {
            return o.facet.startsWith('categories');
          })
          .sort((a, b) => a.value.length - b.value.length)) {
          const splitted = o.value.split(' > ');
          const _id = splitted.pop()?.trim() || '';
          const category = allCategories.find(
            (c) => BbitString.ensureBeginningHashtag(c._id) === BbitString.ensureBeginningHashtag(_id),
          );

          breadcrumbs.push({
            facet: o.facet,
            text: { object: category?.designation || {} },
            value: o.value,
          });
        }

        if (breadcrumbs.length > 0) {
          breadcrumbs.unshift({
            facet: undefined,
            text: { key: 'allProducts' },
          });
        }

        this.breadcrumbs.next(breadcrumbs);
        this._setTitle();
      });

    this.algolia
      .observeQuery()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((query) => {
        this._query = query;
        this._setTitle();
      });
  }

  private _setTitle() {
    if (this._query) {
      this.title.next({ key: 'resultsFor', values: [this._query] });
      this.showResultCount.next(true);
    } else {
      this.title.next(
        this.breadcrumbs.value.length
          ? this.breadcrumbs.value[this.breadcrumbs.value.length - 1].text
          : { key: 'allProducts' },
      );
      this.showResultCount.next(false);
    }
  }

  public ngOnDestroy(): void {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  public breadcrumbClicked(item: IBreadcrumb) {
    if (!item.facet) {
      this.algolia.clearCategoryFilters();
    } else {
      this.algolia.setCategoryFilter(item.facet, item.value || '');
    }
  }
}
